import React from 'react';
import { Heart, Coffee, Users, BookOpen, Mail, Facebook } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { socialLinks } from '../data/mockData';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-50 to-cyan-50 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Coffee className="w-20 h-20 text-teal-600 mx-auto mb-6" />
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            About Two Sandwiches and a Nap
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            A place of hope, support, and healing for anyone affected by addiction.
          </p>
        </div>
      </section>

      {/* The Story */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Our Story</h2>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              "Two Sandwiches and a Nap" started from a simple moment of clarity in early recovery. 
              When everything felt overwhelming and impossible, someone gave me the simplest advice: 
              eat two sandwiches, take a nap, and wake up to try again.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              It sounds almost silly, doesn't it? But in that moment, it was exactly what I needed 
              to hear. Not some grand plan for recovery. Not a complicated 12-step breakdown. Just 
              permission to take care of the basics - to be kind to myself, to rest, and to keep trying.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              That simple phrase became a lifeline. It reminded me that recovery doesn't have to be 
              perfect or complicated. Sometimes the most profound healing comes from the simplest acts 
              of self-compassion.
            </p>

            <div className="bg-teal-50 border-l-4 border-teal-600 p-8 my-10">
              <p className="text-2xl text-teal-900 font-semibold mb-4">
                "Recovery isn't about being perfect. It's about showing up, taking care of yourself, 
                and trying again tomorrow."
              </p>
            </div>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              This website, the upcoming book, and our Facebook community all share the same mission: 
              to remind people struggling with addiction that they're not alone, that recovery is possible, 
              and that sometimes healing starts with the simplest things.
            </p>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Our Mission</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-2 border-gray-200">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Heart className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Provide Hope
                </h3>
                <p className="text-gray-600">
                  Remind everyone struggling that recovery is possible and they are not alone
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Connect to Resources
                </h3>
                <p className="text-gray-600">
                  Make it easy to find AA, NA, crisis support, and other recovery resources
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <BookOpen className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Share Stories
                </h3>
                <p className="text-gray-600">
                  Create a space for honest stories of struggle and recovery to inspire others
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Who This Is For
          </h2>
          
          <div className="space-y-6">
            <Card className="border-l-4 border-teal-600">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  For Anyone Struggling with Addiction
                </h3>
                <p className="text-gray-700">
                  Whether you're just starting to think about recovery or you've been trying for years, 
                  this is a place for you. No judgment, no pressure - just support and resources.
                </p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-teal-600">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  For Loved Ones
                </h3>
                <p className="text-gray-700">
                  If someone you care about is struggling with addiction, you need support too. 
                  Find resources for families and learn how to help while taking care of yourself.
                </p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-teal-600">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  For Those in Recovery
                </h3>
                <p className="text-gray-700">
                  Recovery is a journey, not a destination. This is a place to find ongoing support, 
                  connect with community, and remember you're not alone.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Connect Section */}
      <section className="py-20 bg-gradient-to-br from-teal-50 to-cyan-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Connect With Us
          </h2>
          <p className="text-xl text-gray-600 mb-10">
            Join our community and stay updated on the book, new blog posts, and recovery resources.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={socialLinks.facebook}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="lg" className="w-full sm:w-auto bg-teal-600 hover:bg-teal-700 text-white px-8 py-6 text-lg">
                <Facebook className="mr-2 w-5 h-5" />
                Follow on Facebook
              </Button>
            </a>
            <a href={`mailto:${socialLinks.email}`}>
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-teal-600 text-teal-600 hover:bg-teal-50 px-8 py-6 text-lg">
                <Mail className="mr-2 w-5 h-5" />
                Email Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Final Message */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Heart className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <p className="text-2xl text-gray-800 leading-relaxed italic">
            "Remember: You are worthy of recovery. You are worthy of love. You are worthy of care. 
            And sometimes, all you need is two sandwiches and a nap to start again."
          </p>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
